import { cn } from '@/lib/utils';
import { paymentMethodLabels } from '@/features/dashboard/tourConfig/types/PaymentMethod.utils.ts';
import { PaymentVm } from '@/features/dashboard/bookingChanges/types/BillViewModel.ts';
import { formatFullDateTime } from '@/features/dashboard/bookingChanges/utils/formatters.ts';
import MoneyAmountList from '@/features/dashboard/bookingChanges/components/primitives/MoneyAmountList.tsx';
import RowActionsMenu from '@/components/ui/RowActionsMenu.tsx';
import TagChip from '@/features/dashboard/tags/components/TagChip.tsx';
import PaymentSplitPopover from './PaymentSplitPopover';
import { paymentsGrid, stackedCell } from './paymentTableLayout';

/**
 * Bez statusu: wpłata to zaksięgowana transakcja, więc kolumna zawsze mówiłaby
 * to samo. Zwrot poznaje się po minusie przy kwocie.
 */
const PaymentRow = ({
  payment,
  bookingId,
  onEdit,
  onDelete,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  payment: PaymentVm;
  bookingId: string;
  onEdit: (payment: PaymentVm) => void;
  onDelete: (payment: PaymentVm) => void;
}) => {
  const methodLabel = paymentMethodLabels[payment.method];

  return (
    <div
      role="row"
      className={cn(
        paymentsGrid,
        'items-center border-b border-dashed border-neutral-100',
        className,
      )}
      {...props}
    >
      <div
        role="cell"
        className={cn(
          'py-3.5 text-[15px] text-neutral-800',
          stackedCell.topLeft,
        )}
      >
        {formatFullDateTime(payment.paidAt)}
      </div>
      <div
        role="cell"
        className={cn(
          'flex flex-wrap items-center gap-x-2 gap-y-1 py-3.5 text-[15px] text-ink',
          stackedCell.bottomLeft,
        )}
      >
        <span className="min-w-0">
          {methodLabel}
          {payment.reference && (
            <span className="text-neutral-500"> · {payment.reference}</span>
          )}
        </span>
        {payment.tags.map((tag) => (
          <TagChip key={tag.name} tag={tag} />
        ))}
      </div>
      <div
        role="cell"
        className={cn(
          'flex items-center justify-end gap-1.5 py-3.5 text-right text-[15px] font-semibold tabular-nums text-ink',
          stackedCell.topRight,
        )}
      >
        {payment.transferTotal && (
          <PaymentSplitPopover
            transactionId={payment.id}
            bookingId={bookingId}
          />
        )}
        <MoneyAmountList values={payment.amount} />
      </div>
      <div role="cell" className={cn('py-2.5', stackedCell.actions)}>
        <RowActionsMenu
          label={`${methodLabel}, ${formatFullDateTime(payment.paidAt)}`}
          items={[
            { label: 'Edytuj', onSelect: () => onEdit(payment) },
            {
              label: 'Usuń',
              onSelect: () => onDelete(payment),
              destructive: true,
            },
          ]}
        />
      </div>
    </div>
  );
};

export default PaymentRow;
